import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { clientsAPI } from '../../lib/clientsAPI';
import { ENTITY_LABELS } from '../../lib/taskTemplatesAPI';

// Searchable client picker (server-side search, debounced, first 20 results). onChange(client | null).
export const ClientCombobox = ({ value, label, onChange, placeholder = 'Select client (optional)', testid = 'task-client', status = 'active' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    setBusy(true);
    const h = setTimeout(() => clientsAPI.list({ status: status || undefined, search: q.trim() || undefined, limit: 20 })
      .then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setBusy(false)), 250);
    return () => clearTimeout(h);
  }, [q, open]);
  const pick = (c) => { onChange(c); setOpen(false); setQ(''); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal pr-8" data-testid={`${testid}-combobox`}>
            <span className={cn('truncate', !value && 'text-muted-foreground')}>{value ? label || 'Selected client' : placeholder}</span>
            {!value && <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />}
          </Button>
        </PopoverTrigger>
        {value && (
          <button type="button" onClick={() => onChange(null)} aria-label="Clear client" data-testid={`${testid}-clear`}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted"><X className="w-3.5 h-3.5" /></button>
        )}
      </div>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by name, ABN or email…" value={q} onValueChange={setQ} data-testid={`${testid}-search`} />
          <CommandList className="max-h-72">
            {busy && rows.length === 0 ? <div className="py-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
              : <CommandEmpty data-testid={`${testid}-empty`}>No clients found</CommandEmpty>}
            <CommandGroup>
              {rows.map((c) => (
                <CommandItem key={c.id} value={c.id} onSelect={() => pick(c)} data-testid={`${testid}-option-${c.id}`}>
                  <Check className={cn('w-4 h-4 mr-2 shrink-0', value === c.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate flex-1">{c.display_name}</span>
                  <span className="ml-2 text-xs text-muted-foreground shrink-0">{ENTITY_LABELS[c.entity_type] || c.entity_type}</span>
                </CommandItem>))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
